import React, { useState, useEffect, useContext, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { ArrowLeft, Volume2, Clock, CheckCircle, Play, Pause, RotateCcw } from 'lucide-react';
import { AuthContext } from '../App';
import CabinCrewLayout from '../layouts/CabinCrewLayout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ListeningTest = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const [session, setSession] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(1800); // 30 minutes
  const [loading, setLoading] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioProgress, setAudioProgress] = useState(0);
  const [playCount, setPlayCount] = useState({});
  const audioRef = useRef(null);
  
  useEffect(() => {
    startSession();
  }, []);
  
  useEffect(() => {
    if (session && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(timeLeft - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      handleSubmit();
    }
  }, [timeLeft, session]);
  
  const startSession = async () => {
    try {
      const response = await axios.post(`${API}/listening/start-session`, {}, {
        withCredentials: true
      });
      setSession(response.data);
      setLoading(false);
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error('Önce seviye tespit sınavı yapmalısınız');
        navigate('/dashboard');
      } else {
        toast.error('Oturum başlatılamadı');
      }
    }
  };
  
  const playAudio = async () => {
    const question = session.questions[currentQuestion];
    
    // Max 3 kez dinleme hakkı
    const count = playCount[currentQuestion] || 0;
    if (count >= 3) {
      toast.warning('Bu soruyu en fazla 3 kez dinleyebilirsiniz');
      return;
    }
    
    try {
      setIsPlaying(true);
      
      // Generate audio from text using TTS
      const response = await axios.post(
        `${API}/speaking/text-to-speech`,
        { text: question.audio_text },
        { 
          withCredentials: true,
          responseType: 'blob'
        }
      );
      
      const audioUrl = URL.createObjectURL(response.data);
      const audio = new Audio(audioUrl);
      audioRef.current = audio;
      
      audio.ontimeupdate = () => {
        if (audio.duration) {
          setAudioProgress((audio.currentTime / audio.duration) * 100);
        }
      };
      
      audio.onended = () => {
        setIsPlaying(false);
        setAudioProgress(0);
        URL.revokeObjectURL(audioUrl);
        
        // Increment play count
        setPlayCount({
          ...playCount,
          [currentQuestion]: count + 1
        });
      };
      
      audio.onerror = (e) => {
        console.error('Audio error:', e);
        setIsPlaying(false);
        setAudioProgress(0);
        URL.revokeObjectURL(audioUrl);
        // Use speech synthesis as fallback
        useSpeechSynthesisForListening(question.audio_text, count);
      };
      
      try {
        await audio.play();
      } catch (playError) {
        console.error('Play error:', playError);
        URL.revokeObjectURL(audioUrl);
        useSpeechSynthesisForListening(question.audio_text, count);
      }
    } catch (error) {
      console.error('Audio error:', error);
      useSpeechSynthesisForListening(session.questions[currentQuestion].audio_text, count);
    }
  };

  const useSpeechSynthesisForListening = (text, count) => {
    setIsPlaying(true);
    if ('speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      utterance.rate = 0.9;
      utterance.onend = () => {
        setIsPlaying(false);
        setPlayCount({
          ...playCount,
          [currentQuestion]: count + 1
        });
      };
      utterance.onerror = (e) => {
        console.error('Speech synthesis error:', e);
        setIsPlaying(false);
        toast.info('Ses servisi kullanılamıyor.');
      };
      window.speechSynthesis.speak(utterance);
    } else {
      setIsPlaying(false);
      toast.info('Ses servisi kullanılamıyor.');
    }
  };
  
  const pauseAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
    }
  };
  
  const handleAnswer = (questionId, answer) => {
    setAnswers({
      ...answers,
      [questionId]: answer
    });
  };
  
  const handleNext = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      setAudioProgress(0);
    }
    
    if (currentQuestion < session.questions.length - 1) {
      setCurrentQuestion(currentQuestion + 1);
    }
  };
  
  const handlePrevious = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      setIsPlaying(false);
      setAudioProgress(0);
    }
    
    if (currentQuestion > 0) {
      setCurrentQuestion(currentQuestion - 1);
    }
  };
  
  const handleSubmit = async () => {
    if (audioRef.current) {
      audioRef.current.pause();
    }
    
    try {
      const response = await axios.post(
        `${API}/listening/submit`,
        {
          session_id: session.session_id,
          answers: Object.entries(answers).map(([qId, answer]) => ({
            question_id: parseInt(qId),
            answer: answer
          }))
        },
        { withCredentials: true }
      );
      
      toast.success('Listening testi tamamlandı!');
      navigate(`/listening-results/${session.session_id}`);
    } catch (error) {
      toast.error('Test gönderilemedi');
    }
  };
  
  if (loading) {
    return (
      <CabinCrewLayout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Test hazırlanıyor...</p>
          </div>
        </div>
      </CabinCrewLayout>
    );
  }
  
  if (!session) return null;
  
  const question = session.questions[currentQuestion];
  const progress = ((currentQuestion + 1) / session.questions.length) * 100;
  const answeredCount = Object.keys(answers).length;
  const currentPlayCount = playCount[currentQuestion] || 0;
  
  return (
    <CabinCrewLayout>
      <div className="min-h-screen py-8">
        <div className="container mx-auto px-4 max-w-4xl">
          <div className="flex items-center justify-between mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/dashboard')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Dashboard
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm text-gray-600">
              <CheckCircle className="w-4 h-4" />
              {answeredCount}/{session.questions.length} cevaplandı
            </div>
            <div className="flex items-center gap-2 px-3 py-1 bg-blue-100 rounded-full">
              <Clock className="w-4 h-4 text-blue-600" />
              <span className="text-sm font-semibold text-blue-600">
                {Math.floor(timeLeft / 60)}:{String(timeLeft % 60).padStart(2, '0')}
              </span>
            </div>
          </div>
        </div>
        
        <div className="mb-6">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm text-gray-600">Soru {currentQuestion + 1} / {session.questions.length}</span>
            <span className="text-sm font-semibold text-blue-600">Seviye: {session.level}</span>
          </div>
          <Progress value={progress} />
        </div>
        
        <Card className="glass-effect border-none shadow-xl mb-6">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Volume2 className="w-6 h-6 text-green-600" />
              Listening Comprehension
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Audio Player */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-6 rounded-lg">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-1">Ses Kaydı</p>
                  <p className="text-xs text-gray-600">
                    Dinleme hakkı: {currentPlayCount}/3
                  </p>
                </div>
                
                <div className="flex gap-2">
                  {!isPlaying ? (
                    <Button
                      onClick={playAudio}
                      className="bg-green-600 hover:bg-green-700"
                      disabled={currentPlayCount >= 3}
                    >
                      <Play className="w-4 h-4 mr-2" />
                      {currentPlayCount === 0 ? 'Dinle' : 'Tekrar Dinle'}
                    </Button>
                  ) : (
                    <Button
                      onClick={pauseAudio}
                      className="bg-gray-600 hover:bg-gray-700"
                    >
                      <Pause className="w-4 h-4 mr-2" />
                      Duraklat
                    </Button>
                  )}
                </div>
              </div>
              
              {/* Progress Bar */}
              {isPlaying && (
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-green-600 h-2 rounded-full transition-all"
                    style={{ width: `${audioProgress}%` }}
                  />
                </div>
              )}
            </div>
            
            {/* Question */}
            <div>
              <p className="text-lg font-semibold text-gray-900 mb-4">{question.question}</p>
              
              {/* Multiple Choice Options */}
              <div className="space-y-3">
                {question.options.map((option, idx) => (
                  <label
                    key={idx}
                    className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                      answers[currentQuestion] === option
                        ? 'border-green-500 bg-green-50'
                        : 'border-gray-200 hover:border-green-300'
                    }`}
                  >
                    <input
                      type="radio"
                      name={`question-${currentQuestion}`}
                      value={option}
                      checked={answers[currentQuestion] === option}
                      onChange={(e) => handleAnswer(currentQuestion, e.target.value)}
                      className="mr-3"
                    />
                    <span className="text-gray-700">{option}</span>
                  </label>
                ))}
              </div>
            </div>
            
            {currentPlayCount === 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3">
                <p className="text-sm text-yellow-800">
                  💡 İpucu: Her soruyu en fazla 3 kez dinleyebilirsiniz. Dikkatli dinleyin!
                </p>
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            onClick={handlePrevious}
            disabled={currentQuestion === 0}
            variant="outline"
          >
            Önceki
          </Button>
          
          <div className="flex gap-3">
            {currentQuestion < session.questions.length - 1 ? (
              <Button onClick={handleNext} className="bg-green-600 hover:bg-green-700">
                Sonraki
              </Button>
            ) : (
              <Button 
                onClick={handleSubmit}
                className="bg-blue-600 hover:bg-blue-700"
                disabled={answeredCount < session.questions.length}
              >
                <CheckCircle className="w-4 h-4 mr-2" />
                Testi Bitir
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
    </CabinCrewLayout>
  );
};

export default ListeningTest;
